import { useEffect, useState } from 'react'

export type UseImageWithAspectArgs = {
	src: string | undefined
	defaultAspect?: number
}

export type UseImageWithAspectReturns = {
	isLoading: boolean
	aspectRatio: number
	width: number | undefined
	height: number | undefined
}

/**
 * Hook to track image loading and transition from default to natural aspect ratio.
 * Helps reduce content shift by defaulting to a specified aspect ratio while loading.
 *
 * @param src - The image source URL
 * @param defaultAspect - The default aspect ratio to use while loading (default: 4/5 = 0.8)
 * @returns { isLoading, aspectRatio, width, height } - Loading state, current aspect ratio, and image dimensions
 */
export function useImageWithAspect(
	src: UseImageWithAspectArgs['src'],
	defaultAspect: number = 4 / 5,
): UseImageWithAspectReturns {
	const [isLoading, setIsLoading] = useState(true)
	const [aspectRatio, setAspectRatio] = useState(defaultAspect)
	const [width, setWidth] = useState<number | undefined>(undefined)
	const [height, setHeight] = useState<number | undefined>(undefined)

	useEffect(() => {
		if (!src) {
			setIsLoading(false)
			setWidth(undefined)
			setHeight(undefined)
			return
		}

		setIsLoading(true)
		setAspectRatio(defaultAspect)
		setWidth(undefined)
		setHeight(undefined)

		const img = new Image()
		let isMounted = true

		img.onload = () => {
			if (!isMounted) { return }

			const { naturalWidth, naturalHeight } = img

			// Validate dimensions before computing ratio to avoid NaN
			if (naturalHeight > 0 && Number.isFinite(naturalWidth) && Number.isFinite(naturalHeight)) {
				const natural = naturalWidth / naturalHeight
				setAspectRatio(natural)
				setWidth(naturalWidth)
				setHeight(naturalHeight)
			} else {
				// Fall back to default aspect ratio if dimensions are invalid
				setAspectRatio(defaultAspect)
				setWidth(undefined)
				setHeight(undefined)
			}

			setIsLoading(false)
		}

		img.onerror = () => {
			if (!isMounted) { return }
			setWidth(undefined)
			setHeight(undefined)
			setIsLoading(false)
		}

		img.src = src

		return () => {
			// Prevent state updates after unmount
			isMounted = false
			img.onload = null
			img.onerror = null
			img.src = ''
		}
	}, [src, defaultAspect])

	return { isLoading, aspectRatio, width, height }
}
